import {
  AlertCircle,
  CheckCircle,
  Lock,
  Mail,
  User,
  UserPlus,
} from 'lucide-react'
import type { JSX } from 'react/jsx-runtime'
import { useNavigate } from 'react-router-dom'
import { routes } from '@/common/constants/routes'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSignup } from '@/features/auth/hooks/use-signup'

export const SignupView = (): JSX.Element => {
  const {
    error,
    formData,
    handleInputChange,
    handleSignup,
    isLoading,
    isSuccess,
  } = useSignup()
  const navigate = useNavigate()

  if (isSuccess) {
    return (
      <div
        className="
          flex min-h-screen items-center justify-center bg-gray-50 p-4
          dark:bg-gray-950
        "
      >
        <Card
          className="
            w-full max-w-md border-none shadow-2xl
            dark:bg-gray-900
          "
        >
          <CardHeader className="space-y-1 text-center">
            <div className="mb-4 flex justify-center">
              <div
                className="
                  flex size-12 items-center justify-center rounded-2xl
                  bg-green-600 text-white
                "
              >
                <CheckCircle className="size-6" />
              </div>
            </div>
            <CardTitle
              className="
                text-2xl font-black text-gray-900
                dark:text-gray-100
              "
            >
              Check Your Email
            </CardTitle>
            <CardDescription
              className="
                text-gray-500
                dark:text-gray-400
              "
            >
              We&apos;ve sent you a confirmation link to complete your
              registration.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              className="
                h-12 w-full rounded-xl bg-teal-600 font-bold text-white
                shadow-lg shadow-teal-100 transition-all
                hover:bg-teal-700
                active:scale-95
                dark:shadow-none
              "
              onClick={() => {
                void navigate(routes.LOGIN)
              }}
            >
              Back to Login
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div
      className="
        flex min-h-screen items-center justify-center bg-gray-50 p-4
        dark:bg-gray-950
      "
    >
      <Card
        className="
          w-full max-w-md border-none shadow-2xl
          dark:bg-gray-900
        "
      >
        <CardHeader className="space-y-1 text-center">
          <div className="mb-4 flex justify-center">
            <div
              className="
                flex size-12 items-center justify-center rounded-2xl bg-teal-600
                text-2xl font-bold text-white shadow-lg shadow-teal-100
                dark:shadow-none
              "
            >
              V
            </div>
          </div>
          <CardTitle
            className="
              text-2xl font-black text-gray-900
              dark:text-gray-100
            "
          >
            Join VNM
          </CardTitle>
          <CardDescription
            className="
              text-[10px] font-bold tracking-widest text-gray-500 uppercase
              dark:text-gray-400
            "
          >
            Create your advocate account
          </CardDescription>
        </CardHeader>
        <form
          onSubmit={(event) => {
            void handleSignup(event)
          }}
        >
          <CardContent className="space-y-4">
            {error && (
              <Alert
                variant="destructive"
                className="
                  rounded-xl border-red-100 bg-red-50
                  dark:bg-red-900/10
                "
              >
                <AlertCircle className="size-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label
                htmlFor="fullName"
                className="
                  text-[11px] font-bold tracking-wider text-gray-400 uppercase
                  dark:text-gray-500
                "
              >
                Full Name
              </Label>
              <div className="relative">
                <User className="absolute top-3 left-3 h-4 w-4 text-gray-400" />
                <Input
                  required
                  id="fullName"
                  placeholder="John Doe"
                  type="text"
                  value={formData.fullName}
                  className="
                    h-11 rounded-xl pl-10
                    dark:border-gray-700 dark:bg-gray-800
                  "
                  onChange={handleInputChange('fullName')}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="
                  text-[11px] font-bold tracking-wider text-gray-400 uppercase
                  dark:text-gray-500
                "
              >
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute top-3 left-3 h-4 w-4 text-gray-400" />
                <Input
                  required
                  id="email"
                  placeholder="advocate@example.com"
                  type="email"
                  value={formData.email}
                  className="
                    h-11 rounded-xl pl-10
                    dark:border-gray-700 dark:bg-gray-800
                  "
                  onChange={handleInputChange('email')}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="
                  text-[11px] font-bold tracking-wider text-gray-400 uppercase
                  dark:text-gray-500
                "
              >
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute top-3 left-3 h-4 w-4 text-gray-400" />
                <Input
                  required
                  id="password"
                  placeholder="••••••••"
                  type="password"
                  value={formData.password}
                  className="
                    h-11 rounded-xl pl-10
                    dark:border-gray-700 dark:bg-gray-800
                  "
                  onChange={handleInputChange('password')}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="confirmPassword"
                className="
                  text-[11px] font-bold tracking-wider text-gray-400 uppercase
                  dark:text-gray-500
                "
              >
                Confirm Password
              </Label>
              <div className="relative">
                <Lock className="absolute top-3 left-3 h-4 w-4 text-gray-400" />
                <Input
                  required
                  id="confirmPassword"
                  placeholder="••••••••"
                  type="password"
                  value={formData.confirmPassword}
                  className="
                    h-11 rounded-xl pl-10
                    dark:border-gray-700 dark:bg-gray-800
                  "
                  onChange={handleInputChange('confirmPassword')}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              disabled={isLoading}
              type="submit"
              className="
                h-12 w-full rounded-xl bg-teal-600 font-bold text-white
                shadow-lg shadow-teal-100 transition-all
                hover:bg-teal-700
                active:scale-95
                dark:shadow-none
              "
            >
              {isLoading ? (
                'Creating Account...'
              ) : (
                <>
                  <UserPlus className="mr-2 size-4" /> Sign Up
                </>
              )}
            </Button>
            <div className="text-center text-xs text-gray-500">
              Already have an account?{' '}
              <button
                type="button"
                className="
                  cursor-pointer border-none bg-transparent p-0 font-bold
                  text-teal-600
                  hover:underline
                "
                onClick={() => void navigate(routes.LOGIN)}
              >
                Log in
              </button>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
